// src/pages/Planning.js
// vPLN-013
// - Relaties staan standaard vooraan
// - Regel-tellers verplaatst naar de linker kolomkop
// - Relatie-cellen tonen bij 1 plaatsing de medewerkernaam in plaats van 1
// - Zoeken blijft op dezelfde regel als navigatie
// - Planborddagen worden in de frontend altijd vanaf maandag opgebouwd
// - Personeel-cel toont bij 1 plaatsing de relatie/klantnaam
// - Basisstand toont alleen relevante regels; knop Alle tonen toont alle actieve relaties/personeel
// - Basisstand Personeel filtert lege/vrije cellen nu echt weg
// - Linker kolom Relaties/Personeel heeft dezelfde, smallere breedte
// - Basisstand Personeel negeert nu ook serverregels voor vrij/beschikbaar/nog inplannen
// - Basisstand Personeel telt alleen echte plaatsing/beschikbaarheid of gekleurde gevulde cellen
// - Zoekveld wordt hard leeggemaakt bij Vernieuw en wisselen Relaties/Personeel
// - Legenda tekst "Nog inplannen / vrij" ingekort naar "Nog inplannen"
// - Legenda tekst "Verlof/vakantie" ingekort naar "Verlof"

import React, { useEffect, useMemo, useRef, useState } from "react";

const VERSION = "vPLN-013";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function safeTrim(s) {
  return String(s || "").trim();
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dateToIsoLocal(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseIsoLocal(dateText) {
  const s = isoDate(dateText) || todayIso();
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function todayIso() {
  return dateToIsoLocal(new Date());
}

function addDays(dateText, days) {
  const d = parseIsoLocal(dateText);
  d.setDate(d.getDate() + Number(days || 0));
  return dateToIsoLocal(d);
}

function monday(dateText) {
  const d = parseIsoLocal(dateText);
  const day = d.getDay(); // 0 = zondag, 1 = maandag
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return dateToIsoLocal(d);
}

function isoWeekNumber(dateText) {
  const d = parseIsoLocal(dateText);
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil((((utc - yearStart) / 86400000) + 1) / 7);
}

function dayLabelNL(dateText) {
  const labels = ["zo", "ma", "di", "wo", "do", "vr", "za"];
  return labels[parseIsoLocal(dateText).getDay()] || "";
}

function buildBoardDays(startText, weekCount) {
  const start = monday(startText || todayIso());
  const total = Math.max(1, Number(weekCount || 3)) * 7;
  const arr = [];

  for (let i = 0; i < total; i += 1) {
    const date = addDays(start, i);
    const day = parseIsoLocal(date).getDay();
    arr.push({
      date,
      day_label: dayLabelNL(date),
      date_label: formatDateShort(date),
      is_weekend: day === 0 || day === 6,
    });
  }

  return arr;
}

function buildWeekGroups(startText, weekCount) {
  const start = monday(startText || todayIso());
  const totalWeeks = Math.max(1, Number(weekCount || 3));
  const arr = [];

  for (let i = 0; i < totalWeeks; i += 1) {
    const weekStart = addDays(start, i * 7);
    const weekEnd = addDays(weekStart, 6);
    const days = buildBoardDays(weekStart, 1);

    arr.push({
      week: isoWeekNumber(weekStart),
      start_date: weekStart,
      end_date: weekEnd,
      days,
    });
  }

  return arr;
}

function formatDateNL(iso) {
  const s = isoDate(iso);
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function formatDateShort(iso) {
  const s = isoDate(iso);
  if (!s) return "";
  const [, m, d] = s.split("-");
  return `${d}-${m}`;
}

function nlDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return safeTrim(value);
  try {
    return d.toLocaleString("nl-NL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return safeTrim(value);
  }
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function euro(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n === 0) return "";
  return n.toLocaleString("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function personName(p = {}) {
  const first = safeTrim(p.first_name || "");
  const prefix = safeTrim(p.name_prefix || "");
  const last = safeTrim(p.last_name || "");
  const normal = [first, prefix, last].filter(Boolean).join(" ").trim();
  return safeTrim(normal || p.search_name || p.full_name || p.name || p.label || "");
}

function relationName(r = {}) {
  const isPrivate = String(r.relation_entity_type || "").toLowerCase() === "private";
  if (!isPrivate) return safeTrim(r.name || r.search_name || r.label || "");
  const first = safeTrim(r.first_name || "");
  const prefix = safeTrim(r.name_prefix || "");
  const last = safeTrim(r.last_name || "");
  return safeTrim([first, prefix, last].filter(Boolean).join(" ") || r.search_name || r.name || r.label || "");
}

function isFreePlanningText(value) {
  const s = String(value || "").toLowerCase().trim();

  if (!s) return true;
  if (s === "0") return true;
  if (s === "-") return true;
  if (s === "vrij") return true;
  if (s === "beschikbaar") return true;
  if (s === "nog inplannen") return true;
  if (s === "nog inplannen / vrij") return true;
  if (s === "nog inplannen/vrij") return true;

  return (
    s.includes("nog inplannen") ||
    s.includes(" vrij") ||
    s === "vrij" ||
    s.includes("beschikbaar")
  );
}

function isNeutralPlanningColor(value) {
  const s = String(value || "").toLowerCase().replace(/\s/g, "");

  return (
    !s ||
    s === "transparent" ||
    s === "white" ||
    s === "#fff" ||
    s === "#ffffff" ||
    s === "#fafafa" ||
    s === "#f7f7f7" ||
    s === "#f4f4f4"
  );
}

function isPositivePlanningText(value) {
  const s = String(value || "").toLowerCase().trim();

  if (!s || isFreePlanningText(s)) return false;

  return (
    s.includes("plaats") ||
    s.includes("gepland") ||
    s.includes("ingepland") ||
    s.includes("leegloop") ||
    s.includes("ziek") ||
    s.includes("verlof") ||
    s.includes("vakantie") ||
    s.includes("opleiding") ||
    s.includes("cursus") ||
    s.includes("niet beschikbaar") ||
    s.includes("afwezig") ||
    s.includes("bezet")
  );
}

function hasRealPlanningId(item = {}) {
  return !!(
    item.placement_id ||
    item.placementId ||
    item.plaatsing_id ||
    item.availability_id ||
    item.availabilityId ||
    item.beschikbaarheid_id ||
    item.planning_id ||
    item.planningId ||
    item.absence_id ||
    item.absenceId
  );
}

function itemIsRealPlanning(item = {}) {
  const typeText = [
    item.type,
    item.status,
    item.label,
    item.kind,
  ].map(safeTrim).filter(Boolean).join(" ");

  const fullText = [
    typeText,
    item.job_title,
    item.function_name,
  ].map(safeTrim).filter(Boolean).join(" ");

  const lowType = typeText.toLowerCase().trim();
  const lowFull = fullText.toLowerCase().trim();

  // Namen van medewerker/relatie mogen NIET bepalen of een regel relevant is.
  // De server stuurt bij vrije personeelsregels soms toch een personeelsnaam mee.
  // Daardoor bleef Personeel iedereen tonen.
  if (isFreePlanningText(lowType) || isFreePlanningText(lowFull)) return false;

  if (isPositivePlanningText(lowType) || isPositivePlanningText(lowFull)) return true;

  // Een echte plaatsing/beschikbaarheid heeft een eigen id.
  // Zonder zo'n id is een item met alleen namen meestal een vrije/lege serverregel.
  if (hasRealPlanningId(item)) return true;

  return false;
}

function rowHasPlanning(row = {}) {
  const cells = row?.cells && typeof row.cells === "object" ? row.cells : {};

  return Object.values(cells).some((cell) => {
    if (!cell || typeof cell !== "object") return false;

    const label = safeTrim(cell.label || "");
    const status = safeTrim(cell.status || "");
    const color = safeTrim(cell.color || "");
    const items = Array.isArray(cell.items) ? cell.items : [];
    const combined = `${label} ${status}`.toLowerCase().trim();

    // Echte onderliggende plaatsing/beschikbaarheid wint altijd.
    if (items.some(itemIsRealPlanning)) return true;

    // Vrije/lege/beschikbare cellen tellen niet mee in de basisstand.
    if (isFreePlanningText(combined)) return false;

    // Duidelijke planningsteksten tellen wel mee.
    if (isPositivePlanningText(combined)) return true;

    // Een gekleurde cel met zichtbare inhoud telt mee.
    // Een gekleurde lege cel niet.
    if (!isNeutralPlanningColor(color) && !!(label || status)) return true;

    return false;
  });
}

function organisationRow(o = {}, existing = null) {
  const id = o.id ?? o.organisation_id ?? o.organisatie_id ?? existing?.id;
  const label = relationName(o) || existing?.label || `Relatie ${id || ""}`;
  const subtitleParts = [
    safeTrim(o.type || o.relation_type || o.soort || ""),
    safeTrim(o.visit_city || o.city || o.place || o.plaats || ""),
    safeTrim(o.visit_region || o.region || o.regio || ""),
  ].filter(Boolean);

  return {
    ...(existing || {}),
    id,
    label,
    subtitle: existing?.subtitle || subtitleParts.join(" · ") || "Klant / opdrachtgever",
    cells: existing?.cells || {},
  };
}

function personnelRow(p = {}, existing = null) {
  const id = p.id ?? p.personnel_id ?? p.personeel_id ?? existing?.id;
  const label = personName(p) || existing?.label || `Personeel ${id || ""}`;
  const subtitleParts = [
    safeTrim(p.job_functions || p.function_name || p.job_title || p.functie || ""),
    safeTrim(p.visit_city || p.city || p.place || p.plaats || ""),
    safeTrim(p.visit_region || p.region || p.regio || ""),
  ].filter(Boolean);

  return {
    ...(existing || {}),
    id,
    label,
    subtitle: existing?.subtitle || subtitleParts.join(" · ") || "Personeel",
    cells: existing?.cells || {},
  };
}

function getSelectedCompanyCodeIdsHeader() {
  return (
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    sessionStorage.getItem("selectedCompanyCodeIds") ||
    localStorage.getItem("selectedCompanyCodeIds") ||
    ""
  );
}

async function tryFetchJson(urls, options = {}) {
  const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
  const tenant = sessionStorage.getItem("tenant") || localStorage.getItem("tenant") || "demo";

  let parsedUser = {};
  try {
    parsedUser = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "{}");
  } catch {}

  let lastErr = null;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": parsedUser?.role || "user",
        "x-staffiq-user-id": parsedUser?.id || undefined,
        "x-staffiq-user-email": parsedUser?.email || undefined,
        "x-staffiq-user-name": parsedUser?.display_name || parsedUser?.name || undefined,
        "x-company-code-ids": getSelectedCompanyCodeIdsHeader() || undefined,
      };

      Object.keys(headers).forEach((k) => headers[k] === undefined && delete headers[k]);

      const res = await fetch(buildUrl(url), { ...options, headers });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;
        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }
        throw new Error(msg);
      }

      const text = await res.text();
      return text ? JSON.parse(text) : null;
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function InfoRow({ label, value }) {
  const v = safeTrim(value);
  if (!v) return null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "155px 1fr", gap: 10, padding: "5px 0", borderBottom: "1px solid #f1f1f1" }}>
      <div style={{ fontWeight: 700, color: "#444" }}>{label}</div>
      <div style={{ whiteSpace: "pre-wrap" }}>{v}</div>
    </div>
  );
}

function Modal({ title, subtitle, children, onClose, width = "min(980px, 96vw)" }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(0,0,0,0.42)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxHeight: "92vh",
          overflow: "hidden",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.28)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid #e6e6e6",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 20 }}>{title}</h2>
            {subtitle && <div style={{ fontSize: 13, opacity: 0.7, marginTop: 3 }}>{subtitle}</div>}
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "#1f4e79",
              color: "white",
              borderRadius: 6,
              padding: "9px 14px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Sluiten
          </button>
        </div>

        <div style={{ padding: 16, overflowY: "auto" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Planning() {
  const [view, setView] = useState("customer");
  const [startDate, setStartDate] = useState(monday(todayIso()));
  const [weeks, setWeeks] = useState(3);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cellPopup, setCellPopup] = useState(null);
  const [recordPopup, setRecordPopup] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [recordError, setRecordError] = useState("");
  const [q, setQ] = useState("");
  const [rowCounts, setRowCounts] = useState({ personnel: null, customer: null });
  const [showAllRows, setShowAllRows] = useState(false);
  const [allRowsCache, setAllRowsCache] = useState({ personnel: null, customer: null });

  const boardScrollRef = useRef(null);
  const searchInputRef = useRef(null);

  async function loadAllRowsForView(kind) {
    const effectiveKind = kind === "customer" ? "customer" : "personnel";

    if (allRowsCache[effectiveKind]) return allRowsCache[effectiveKind];

    if (effectiveKind === "customer") {
      const data = await tryFetchJson(["/api/organisations"]);
      const rows = (Array.isArray(data) ? data : [])
        .filter((x) => parseActiveValue(x) !== false)
        .map((x) => organisationRow(x))
        .filter((x) => x.id);

      setAllRowsCache((prev) => ({ ...prev, customer: rows }));
      return rows;
    }

    const data = await tryFetchJson(["/api/personnel", "/api/personeel"]);
    const rows = (Array.isArray(data) ? data : [])
      .filter((x) => parseActiveValue(x) !== false)
      .map((x) => personnelRow(x))
      .filter((x) => x.id);

    setAllRowsCache((prev) => ({ ...prev, personnel: rows }));
    return rows;
  }

  async function loadBoard(startOverride = null, viewOverride = null, weeksOverride = null) {
    setLoading(true);
    setError("");

    try {
      const effectiveView = viewOverride || view;
      const effectiveStart = monday(startOverride || startDate || todayIso());
      const effectiveWeeks = Number(weeksOverride || weeks || 3);

      const params = new URLSearchParams();
      params.set("view", effectiveView === "customer" ? "customer" : "personnel");
      params.set("start", effectiveStart);
      params.set("weeks", String(effectiveWeeks));

      const data = await tryFetchJson([`/api/planning/board?${params.toString()}`, `/api/planning-bord?${params.toString()}`]);
      setBoard(data || null);
      setCellPopup(null);
      setStartDate(effectiveStart);
      setRowCounts((prev) => ({
        ...prev,
        [effectiveView === "customer" ? "customer" : "personnel"]: Array.isArray(data?.rows) ? data.rows.length : 0,
      }));
    } catch (e) {
      setError(e?.message || String(e));
      setBoard(null);
      setCellPopup(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadRowCounts(startOverride = null, weeksOverride = null) {
    const effectiveStart = monday(startOverride || startDate || todayIso());
    const effectiveWeeks = Number(weeksOverride || weeks || 3);

    try {
      const nextCounts = {};

      for (const countView of ["personnel", "customer"]) {
        const params = new URLSearchParams();
        params.set("view", countView);
        params.set("start", effectiveStart);
        params.set("weeks", String(effectiveWeeks));

        const data = await tryFetchJson([`/api/planning/board?${params.toString()}`, `/api/planning-bord?${params.toString()}`]);
        nextCounts[countView] = Array.isArray(data?.rows) ? data.rows.length : 0;
      }

      setRowCounts(nextCounts);
    } catch {
      // Tellers mogen het planbord niet blokkeren.
    }
  }

  useEffect(() => {
    const normalizedStart = monday(startDate || todayIso());

    if (normalizedStart !== startDate) {
      setStartDate(normalizedStart);
      return;
    }

    loadBoard(normalizedStart, view, weeks);
    loadRowCounts(normalizedStart, weeks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, startDate, weeks, showAllRows]);

  const rows = useMemo(() => {
    const query = q.toLowerCase().trim();
    const boardRows = Array.isArray(board?.rows) ? board.rows : [];
    let base = boardRows;

    if (showAllRows) {
      const kind = view === "customer" ? "customer" : "personnel";
      const allRows = Array.isArray(allRowsCache[kind]) ? allRowsCache[kind] : [];
      const existingById = new Map(boardRows.map((row) => [String(row.id), row]));

      base = allRows.map((row) => {
        const existing = existingById.get(String(row.id));
        if (!existing) return row;
        return view === "customer" ? organisationRow(row, existing) : personnelRow(row, existing);
      });
    } else {
      base = boardRows.filter(rowHasPlanning);
    }

    if (!query) return base;

    return base.filter((row) => {
      const text = [row.label, row.subtitle].join(" ").toLowerCase();
      return text.includes(query);
    });
  }, [board, q, showAllRows, allRowsCache, view]);

  // Het planbord moet visueel altijd met maandag beginnen.
  // Daarom bouwen we de zichtbare dagen/weekkoppen lokaal op basis van startDate,
  // en gebruiken we de serverdata alleen voor de cellen per datum.
  const days = useMemo(() => buildBoardDays(startDate, weeks), [startDate, weeks]);
  const weekGroups = useMemo(() => buildWeekGroups(startDate, weeks), [startDate, weeks]);
  const isMobile = window.innerWidth <= 768;

  const LEFT_W = 235;
  const CELL_W = isMobile ? 70 : 78;
  const DAY_H = 28;
  const ROW_H = 34;

  const buttonStyle = { padding: "7px 12px", border: "1px solid #d9d9d9", borderRadius: 4, background: "white", cursor: "pointer", whiteSpace: "nowrap" };
  const activeButtonStyle = { ...buttonStyle, background: "#1f4e79", color: "white" };
  const inputStyle = { padding: "7px 10px", border: "1px solid #d9d9d9", borderRadius: 4, background: "white", boxSizing: "border-box" };

  function openCell(row, day, cell) {
    setCellPopup({ row, day, cell: cell || {} });
  }

  function cellTitle(row, day, cell = {}) {
    const parts = [row.label, formatDateNL(day.date), cell.status, cellDisplayLabel(cell)].filter(Boolean);
    return parts.join(" - ");
  }

  function prevPeriod() {
    setStartDate((d) => monday(addDays(monday(d || todayIso()), -7)));
  }

  function nextPeriod() {
    setStartDate((d) => monday(addDays(monday(d || todayIso()), 7)));
  }

  function today() {
    setStartDate(monday(todayIso()));
  }

  function clearSearchField() {
    setQ("");

    // Extra hard leegmaken, zodat ook een browser/autofill-waarde direct verdwijnt.
    try {
      if (searchInputRef.current) searchInputRef.current.value = "";
    } catch {}
  }

  function switchPlanningView(nextView) {
    clearSearchField();
    setShowAllRows(false);
    setView(nextView);
  }

  function refreshCurrentWeek() {
    const currentMonday = monday(todayIso());
    clearSearchField();
    setShowAllRows(false);
    setStartDate(currentMonday);
    setView("customer");
    loadBoard(currentMonday, "customer", weeks);
    loadRowCounts(currentMonday, weeks);
  }

  async function showAllActiveRows() {
    try {
      setError("");
      await loadAllRowsForView(view);
      setShowAllRows(true);
    } catch (err) {
      setError(err?.message || "Alle regels konden niet worden geladen.");
    }
  }

  async function openRowCard(row) {
    if (!row?.id) return;

    setRecordLoading(true);
    setRecordError("");
    setRecordPopup({
      kind: view === "customer" ? "relation" : "personnel",
      row,
      data: null,
    });

    try {
      if (view === "customer") {
        const data = await tryFetchJson([`/api/organisations/${encodeURIComponent(row.id)}`]);
        setRecordPopup({ kind: "relation", row, data: data || row });
      } else {
        const data = await tryFetchJson([`/api/personnel/${encodeURIComponent(row.id)}`, `/api/personeel/${encodeURIComponent(row.id)}`]);
        const person = data?.person || data?.personeel || data || row;
        setRecordPopup({ kind: "personnel", row, data: person, matching: Array.isArray(data?.matching) ? data.matching : [] });
      }
    } catch (err) {
      setRecordError(err?.message || "Kaart kon niet worden geopend.");
      setRecordPopup({ kind: view === "customer" ? "relation" : "personnel", row, data: row });
    } finally {
      setRecordLoading(false);
    }
  }

  function rowHeaderLabel() {
    return view === "customer" ? "Relatie" : "Personeel";
  }

  function cellDisplayLabel(cell = {}) {
    const raw = safeTrim(cell.label || "");
    const items = Array.isArray(cell.items) ? cell.items : [];
    const placementItems = items.filter((it) => it?.type !== "beschikbaarheid");

    if (!raw || raw !== "1" || placementItems.length !== 1) return raw;

    if (view === "customer") {
      return safeTrim(
        placementItems[0].personnel_name ||
        placementItems[0].employee_name ||
        placementItems[0].person_name ||
        placementItems[0].search_name ||
        ""
      ) || raw;
    }

    return safeTrim(
      placementItems[0].organisation_name ||
      placementItems[0].relation_name ||
      placementItems[0].customer_name ||
      placementItems[0].client_name ||
      ""
    ) || raw;
  }

  function compactLegendLabel(label) {
    const s = safeTrim(label);
    const low = s.toLowerCase();

    if (low === "nog inplannen / vrij" || low === "nog inplannen/vrij") {
      return "Nog inplannen";
    }

    if (low === "verlof/vakantie" || low === "verlof / vakantie") {
      return "Verlof";
    }

    return s;
  }

  function renderCellPopup() {
    if (!cellPopup) return null;

    const { row, day, cell } = cellPopup;
    const items = Array.isArray(cell?.items) ? cell.items : [];

    return (
      <Modal
        title="Cel-informatie"
        subtitle={`${rowHeaderLabel()}: ${row.label} · ${formatDateNL(day.date)}`}
        onClose={() => setCellPopup(null)}
        width="min(980px, 96vw)"
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
            <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 10, background: "#fafafa" }}>
              <b>{rowHeaderLabel()}</b><br />
              {row.label}<br />
              <span style={{ color: "#666" }}>{row.subtitle || ""}</span>
            </div>

            <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 10, background: "#fafafa" }}>
              <b>Datum</b><br />
              {formatDateNL(day.date)}<br />
              <span style={{ color: "#666" }}>{day.day_label || ""}</span>
            </div>

            <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 10, background: "#fafafa" }}>
              <b>Status cel</b><br />
              {compactLegendLabel(cell.label || "Nog inplannen")}<br />
              <span style={{ color: "#666" }}>{cell.status || ""}</span>
            </div>
          </div>

          {items.length > 0 ? (
            <div>
              <h3 style={{ marginTop: 4 }}>Achterliggende regels</h3>

              <div style={{ display: "grid", gap: 10 }}>
                {items.map((it, idx) => (
                  <div key={idx} style={{ border: "1px solid #e2e2e2", borderRadius: 8, padding: 12, background: "#fff" }}>
                    {it.type === "beschikbaarheid" ? (
                      <>
                        <h4 style={{ margin: "0 0 8px 0" }}>{it.label || "Beschikbaarheid"}</h4>
                        <InfoRow label="Soort" value={it.status || it.label} />
                        <InfoRow label="Van" value={formatDateNL(it.start_date)} />
                        <InfoRow label="Tot" value={formatDateNL(it.end_date)} />
                        <InfoRow label="Uren per dag" value={it.hours_per_day} />
                        <InfoRow label="Opmerking" value={it.note || it.remarks || it.description} />
                        <InfoRow label="Id" value={it.id || it.availability_id} />
                      </>
                    ) : (
                      <>
                        <h4 style={{ margin: "0 0 8px 0" }}>{it.organisation_name || it.relation_name || "Plaatsing"}</h4>
                        <InfoRow label="Plaatsing-id" value={it.placement_id || it.id} />
                        <InfoRow label="Plaatsingsnummer" value={it.placement_number} />
                        <InfoRow label="Relatie" value={it.organisation_name || it.relation_name} />
                        <InfoRow label="Personeel" value={it.personnel_name || it.employee_name} />
                        <InfoRow label="Functie" value={it.job_title || it.function_name} />
                        <InfoRow label="Status" value={it.status} />
                        <InfoRow label="Startdatum" value={formatDateNL(it.start_date)} />
                        <InfoRow label="Einddatum" value={formatDateNL(it.end_date)} />
                        <InfoRow label="Uren per week" value={it.hours_per_week} />
                        <InfoRow label="Werktijden" value={it.schedule_text} />
                        <InfoRow label="Locatie" value={it.location} />
                        <InfoRow label="Plaats" value={it.place} />
                        <InfoRow label="Regio" value={it.region} />
                        <InfoRow label="CAO" value={it.cao} />
                        <InfoRow label="Loonperiode" value={it.wage_period} />
                        <InfoRow label="Factuurperiode" value={it.invoice_period} />
                        <InfoRow label="Uurloon" value={euro(it.hourly_wage) || it.hourly_wage} />
                        <InfoRow label="Verkooptarief" value={euro(it.sales_rate) || it.sales_rate} />
                        <InfoRow label="Opmerking" value={it.external_note || it.internal_note || it.remarks} />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ color: "#666", border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 }}>
              Geen achterliggende regels voor deze cel.
            </div>
          )}
        </div>
      </Modal>
    );
  }

  function renderRecordPopup() {
    if (!recordPopup) return null;

    const isRelation = recordPopup.kind === "relation";
    const data = recordPopup.data || recordPopup.row || {};
    const title = isRelation ? "Relatiekaart" : "Personeelskaart";
    const subtitle = isRelation ? relationName(data) || recordPopup.row?.label : personName(data) || recordPopup.row?.label;

    return (
      <Modal title={title} subtitle={subtitle} onClose={() => setRecordPopup(null)} width="min(1120px, 96vw)">
        {recordLoading && <div style={{ marginBottom: 10 }}>Kaart wordt geladen...</div>}
        {recordError && <div style={{ color: "crimson", marginBottom: 10 }}>{recordError}</div>}

        {isRelation ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14 }}>
            <div>
              <h3>Algemeen</h3>
              <InfoRow label="Naam" value={data.name || relationName(data)} />
              <InfoRow label="Zoeknaam" value={data.search_name} />
              <InfoRow label="Relatievorm" value={String(data.relation_entity_type || "company").toLowerCase() === "private" ? "Particulier" : "Organisatie"} />
              <InfoRow label="Soort relatie" value={data.type} />
              <InfoRow label="Administratie" value={data.administration_type} />
              <InfoRow label="Bedrijfscode" value={data.company_code_name || data.company_code || data.company_code_display} />
              <InfoRow label="Actief" value={parseActiveValue(data) === false ? "Nee" : "Ja"} />
            </div>

            <div>
              <h3>Contact</h3>
              <InfoRow label="T.a.v." value={data.attention_to} />
              <InfoRow label="Telefoon" value={data.phone} />
              <InfoRow label="Mobiel" value={data.mobile} />
              <InfoRow label="E-mail" value={data.email} />
              <InfoRow label="Website" value={data.website} />
              <InfoRow label="KvK" value={data.kvk} />
              <InfoRow label="BTW nummer" value={data.vat_number} />
            </div>

            <div>
              <h3>Adres / opmerking</h3>
              <InfoRow label="Bezoekadres" value={[data.visit_street, data.visit_house_number, data.visit_addition, data.visit_postcode, data.visit_city, data.visit_region, data.visit_country].filter(Boolean).join(" ")} />
              <InfoRow label="Correspondentie" value={[data.corr_street, data.corr_house_number, data.corr_addition, data.corr_postcode, data.corr_city, data.corr_region, data.corr_country].filter(Boolean).join(" ")} />
              <InfoRow label="Opmerking" value={data.remarks} />
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 150px", gap: 14 }}>
            <div>
              <h3>Naam / privé</h3>
              <InfoRow label="Naam" value={personName(data)} />
              <InfoRow label="Zoeknaam" value={data.search_name} />
              <InfoRow label="Geslacht" value={data.gender} />
              <InfoRow label="Geboortedatum" value={formatDateNL(data.birth_date)} />
              <InfoRow label="Nationaliteit" value={data.nationality} />
              <InfoRow label="Geboorteplaats" value={data.birth_place} />
            </div>

            <div>
              <h3>HRM</h3>
              <InfoRow label="Status" value={data.status} />
              <InfoRow label="Functie" value={data.job_functions} />
              <InfoRow label="Bedrijfscode" value={data.company_code_name || data.company_code || data.bedrijfscode} />
              <InfoRow label="Uurloon" value={euro(data.hourly_wage) || data.hourly_wage} />
              <InfoRow label="Verkooptarief" value={euro(data.sales_rate) || data.sales_rate} />
              <InfoRow label="Rijbewijs" value={data.driving_license} />
              <InfoRow label="Eigen vervoer" value={data.own_transport === true ? "Ja" : "Nee"} />
              <InfoRow label="ZZP" value={data.zzp === true ? "Ja" : "Nee"} />
            </div>

            <div>
              <h3>Contact / adres</h3>
              <InfoRow label="Telefoon" value={data.phone} />
              <InfoRow label="Mobiel" value={data.mobile} />
              <InfoRow label="E-mail" value={data.email} />
              <InfoRow label="Bezoekadres" value={[data.visit_street, data.visit_house_number, data.visit_addition, data.visit_postcode, data.visit_city, data.visit_region, data.visit_country].filter(Boolean).join(" ")} />
              <InfoRow label="Correspondentie" value={[data.corr_street, data.corr_house_number, data.corr_addition, data.corr_postcode, data.corr_city, data.corr_region, data.corr_country].filter(Boolean).join(" ")} />
              <InfoRow label="Opmerking" value={data.remarks} />
            </div>

            <div>
              <h3>Pasfoto</h3>
              {data.id && data.profile_photo_path ? (
                <img
                  src={buildUrl(`/api/personnel/${encodeURIComponent(data.id)}/photo`)}
                  alt="Pasfoto"
                  style={{ width: 128, height: 166, objectFit: "cover", border: "1px solid #d9d9d9", borderRadius: 8 }}
                />
              ) : (
                <div style={{ width: 128, height: 166, border: "1px solid #d9d9d9", borderRadius: 8, background: "#f7f7f7", display: "flex", alignItems: "center", justifyContent: "center", color: "#777" }}>
                  Geen foto
                </div>
              )}
            </div>
          </div>
        )}

        {Array.isArray(recordPopup.matching) && recordPopup.matching.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h3>Matching gegevens</h3>
            <div style={{ display: "grid", gap: 8 }}>
              {recordPopup.matching.map((m) => (
                <div key={m.id || `${m.category}-${m.name}`} style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 10, background: "#fafafa" }}>
                  <b>{m.category || "-"}</b> · {m.name || "-"}
                  {m.level ? ` · ${m.level}` : ""}
                  {m.description ? <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{m.description}</div> : null}
                  <div style={{ color: "#666", marginTop: 4 }}>
                    {[formatDateNL(m.date_from), formatDateNL(m.date_to)].filter(Boolean).join(" t/m ")}
                    {m.diploma ? " · Diploma" : ""}
                    {m.employer ? ` · ${m.employer}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    );
  }


  function displayCount(kind) {
    if (kind === view) return rows.length;
    const value = rowCounts[kind];
    return value === null || value === undefined ? "-" : value;
  }

  const countBadgeStyle = {
    minWidth: 42,
    height: 32,
    padding: "0 10px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "#f7fafc",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    color: "#1f4e79",
    boxSizing: "border-box",
  };

  return (
    <div>
      <h1 style={{ marginTop: 0, marginBottom: 10 }}>Planning <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span></h1>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <button onClick={() => switchPlanningView("customer")} style={view === "customer" ? activeButtonStyle : buttonStyle}>🏢 Relaties</button>
        <button onClick={() => switchPlanningView("personnel")} style={view === "personnel" ? activeButtonStyle : buttonStyle}>👤 Personeel</button>
        <button onClick={prevPeriod} style={buttonStyle}>◀ Week terug</button>
        <button onClick={today} style={buttonStyle}>Vandaag</button>
        <button onClick={nextPeriod} style={buttonStyle}>Week vooruit ▶</button>

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 13 }}>Start</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(monday(e.target.value))} style={inputStyle} />
        </label>

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 13 }}>Weken</span>
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value) || 3)} style={inputStyle}>
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5</option>
            <option value={6}>6</option>
            <option value={8}>8</option>
            <option value={10}>10</option>
            <option value={12}>12</option>
          </select>
        </label>

        <input
          ref={searchInputRef}
          key={`planning-search-${view}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Zoek..."
          autoComplete="off"
          style={{ ...inputStyle, minWidth: 180 }}
        />
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8, alignItems: "center" }}>
        {(board?.legend || []).map((l) => (
          <span key={l.status} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <span style={{ width: 16, height: 16, border: "1px solid #bbb", background: l.color, display: "inline-block" }} />
            {compactLegendLabel(l.label)}
          </span>
        ))}

        <button
          onClick={showAllActiveRows}
          disabled={showAllRows}
          title={view === "customer" ? "Toon alle actieve relaties" : "Toon alle actieve personeelsleden"}
          style={{ ...buttonStyle, marginLeft: 6, opacity: showAllRows ? 0.55 : 1, cursor: showAllRows ? "not-allowed" : "pointer" }}
        >
          Alle tonen
        </button>

        <button onClick={refreshCurrentWeek} style={buttonStyle}>🔄 Vernieuw</button>
      </div>

      {error && <div style={{ color: "#b00020", marginBottom: 8 }}>{error}</div>}
      {loading && <div style={{ marginBottom: 8 }}>Planning wordt geladen...</div>}

      <div style={{ minWidth: 0 }}>
        <div ref={boardScrollRef} style={{ border: "1px solid #d9d9d9", borderRadius: 6, background: "white", overflow: "auto", maxHeight: "calc(100dvh - 150px)" }}>
          <div style={{ minWidth: LEFT_W + days.length * CELL_W }}>
            <div style={{ display: "grid", gridTemplateColumns: `${LEFT_W}px repeat(${days.length}, ${CELL_W}px)`, position: "sticky", top: 0, zIndex: 5 }}>
              <div style={{ gridRow: "1 / span 2", position: "sticky", left: 0, zIndex: 8, background: "#f7f7f7", borderRight: "1px solid #cfcfcf", borderBottom: "1px solid #cfcfcf", padding: "8px 10px", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                <span>{rowHeaderLabel()}</span>
                <span title={`Aantal regels in ${rowHeaderLabel()}`} style={{ ...countBadgeStyle, height: 24, minWidth: 38, padding: "0 8px", fontSize: 12 }}>
                  {displayCount(view === "customer" ? "customer" : "personnel")}
                </span>
              </div>

              {weekGroups.map((w, wi) => (
                <div key={`${w.week}-${wi}`} style={{ gridColumn: `${2 + wi * 7} / span ${w.days?.length || 7}`, background: "#ececec", borderRight: "1px solid #cfcfcf", borderBottom: "1px solid #cfcfcf", textAlign: "center", fontWeight: 700, fontSize: 12, height: DAY_H, lineHeight: `${DAY_H}px` }}>
                  Week {w.week} &nbsp; {formatDateShort(w.start_date)} t/m {formatDateShort(w.end_date)}
                </div>
              ))}

              {days.map((d) => (
                <div key={d.date} style={{ background: d.is_weekend ? "#f4f4f4" : "#fff", borderRight: "1px solid #e1e1e1", borderBottom: "1px solid #cfcfcf", textAlign: "center", fontSize: 11, height: DAY_H, lineHeight: "1.15", paddingTop: 3, boxSizing: "border-box" }}>
                  <div>{d.day_label}</div>
                  <div>{d.date_label}</div>
                </div>
              ))}
            </div>

            {rows.length === 0 ? (
              <div style={{ padding: 14, color: "#666" }}>Geen regels gevonden.</div>
            ) : rows.map((row) => (
              <div key={row.id} style={{ display: "grid", gridTemplateColumns: `${LEFT_W}px repeat(${days.length}, ${CELL_W}px)`, minHeight: ROW_H }}>
                <button
                  type="button"
                  onClick={() => openRowCard(row)}
                  title={`${rowHeaderLabel()} openen: ${row.label}`}
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    background: "white",
                    border: "none",
                    borderRight: "1px solid #cfcfcf",
                    borderBottom: "1px solid #efefef",
                    padding: "5px 8px",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    textAlign: "left",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111" }}>{row.label}</div>
                  <div style={{ fontSize: 11, color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.subtitle}</div>
                </button>

                {days.map((d) => {
                  const cell = row.cells?.[d.date] || {};
                  return (
                    <button
                      key={`${row.id}-${d.date}`}
                      type="button"
                      title={cellTitle(row, d, cell)}
                      onClick={() => openCell(row, d, cell)}
                      style={{
                        height: ROW_H,
                        border: "1px solid #e1e1e1",
                        background: cell.color || (d.is_weekend ? "#fafafa" : "#fff"),
                        color: cell.textColor || "#111",
                        fontSize: 11,
                        padding: 2,
                        cursor: "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                      }}
                    >
                      {cellDisplayLabel(cell)}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {renderCellPopup()}
      {renderRecordPopup()}
    </div>
  );
}
